import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { getRmaJobs, updateRmaJobStatus } from '../utils/rmaDb';
import type { RmaJob, RmaStatus, RmaPriority, DeviceCategory } from '../utils/rmaDb';
import {
  Search,
  Plus,
  Eye,
  Printer,
  SlidersHorizontal,
  RefreshCw,
  Smartphone,
  Laptop,
  Tablet,
  Gamepad2,
  HelpCircle,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';

const RmaJobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<RmaJob[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [priorityFilter, setPriorityFilter] = useState<string>('All');
  const [categoryFilter, setCategoryFilter] = useState<string>('All');
  
  // Quick status update popover state
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = () => {
    setJobs(getRmaJobs());
  };

  const handleStatusChange = (id: string, newStatus: RmaStatus) => {
    const updated = updateRmaJobStatus(id, newStatus, 'System Admin', 'Quick status update from directory board');
    if (updated) {
      toast.success(`Ticket ${id} status updated to ${newStatus}`);
      loadJobs();
      setActiveMenuId(null);
    } else {
      toast.error('Failed to update status');
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('All');
    priorityFilter !== 'All' && setPriorityFilter('All');
    categoryFilter !== 'All' && setCategoryFilter('All');
    toast.success('Filters cleared');
  };

  // Filter logic
  const filteredJobs = jobs.filter(job => {
    const matchesSearch = 
      job.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.customerPhone.includes(searchTerm) ||
      job.deviceModel.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.serialNumber.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesStatus = statusFilter === 'All' || job.status === statusFilter;
    const matchesPriority = priorityFilter === 'All' || job.priority === priorityFilter;
    const matchesCategory = categoryFilter === 'All' || job.deviceCategory === categoryFilter;

    return matchesSearch && matchesStatus && matchesPriority && matchesCategory;
  });

  // Category Icon helper
  const getCategoryIcon = (category: DeviceCategory) => {
    switch (category) {
      case 'Phone': return <Smartphone className="h-4 w-4" />;
      case 'Laptop': return <Laptop className="h-4 w-4" />;
      case 'Tablet': return <Tablet className="h-4 w-4" />;
      case 'Console': return <Gamepad2 className="h-4 w-4" />;
      default: return <HelpCircle className="h-4 w-4" />;
    }
  };

  // Badge helpers
  const getStatusBadgeClass = (status: RmaStatus) => {
    const styles = {
      'Intake': 'bg-slate-100 text-slate-800 border-slate-200',
      'Diagnostics': 'bg-amber-100 text-amber-800 border-amber-200',
      'Awaiting Parts': 'bg-orange-100 text-orange-800 border-orange-200',
      'Repairing': 'bg-blue-100 text-blue-800 border-blue-200',
      'Testing': 'bg-purple-100 text-purple-800 border-purple-200',
      'Ready': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'Delivered': 'bg-teal-100 text-teal-800 border-teal-200'
    };
    return styles[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getPriorityBadgeClass = (priority: RmaPriority) => {
    const styles = {
      'Low': 'bg-gray-100 text-gray-700',
      'Medium': 'bg-blue-50 text-blue-700',
      'High': 'bg-orange-50 text-orange-700',
      'Urgent': 'bg-rose-50 text-rose-700 border border-rose-100 animate-pulse'
    };
    return styles[priority] || 'bg-gray-100 text-gray-700';
  };

  const statuses: RmaStatus[] = ['Intake', 'Diagnostics', 'Awaiting Parts', 'Repairing', 'Testing', 'Ready', 'Delivered'];
  const priorities: RmaPriority[] = ['Low', 'Medium', 'High', 'Urgent'];
  const categories: DeviceCategory[] = ['Phone', 'Laptop', 'Tablet', 'Console', 'Other'];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col w-full text-slate-800">
      <div className="flex flex-1 w-full">
        <Sidebar />
        
        <main className="flex-1 p-6 md:p-8 w-full max-h-screen overflow-y-auto">
          <div className="w-full space-y-6">
            
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900">Repair Jobs Directory</h1>
                <p className="mt-1 text-sm text-slate-500">
                  Manage repair intakes, update technician diagnostics, and track device status timelines.
                </p>
              </div>
              <button
                onClick={() => navigate('/new-rma-job')}
                className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-white bg-blue-600 hover:bg-blue-700 font-semibold text-sm shadow-md hover:shadow-lg transition-all duration-200"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Intake Ticket
              </button>
            </div>

            {/* Filter Panel */}
            <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm space-y-4">
              
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Search Bar */}
                <div className="flex-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4.5 w-4.5 text-slate-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by RMA #, customer, model, or serial..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50 hover:bg-white transition-colors"
                  />
                  {searchTerm && (
                    <button 
                      onClick={() => setSearchTerm('')} 
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap gap-3">
                  {/* Status Dropdown */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider hidden sm:inline">Status:</span>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none"
                    >
                      <option value="All">All Statuses</option>
                      {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  {/* Priority Dropdown */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider hidden sm:inline">Priority:</span>
                    <select
                      value={priorityFilter}
                      onChange={(e) => setPriorityFilter(e.target.value)}
                      className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none"
                    >
                      <option value="All">All Priorities</option>
                      {priorities.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>

                  {/* Category Dropdown */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider hidden sm:inline">Type:</span>
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none"
                    >
                      <option value="All">All Categories</option>
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>

                  {/* Clear Filter button */}
                  {(searchTerm || statusFilter !== 'All' || priorityFilter !== 'All' || categoryFilter !== 'All') && (
                    <button
                      onClick={resetFilters}
                      className="inline-flex items-center justify-center p-2 rounded-xl text-slate-500 hover:text-rose-600 bg-slate-100 hover:bg-rose-50 transition-colors"
                      title="Clear Filters"
                    >
                      <RefreshCw className="h-4.5 w-4.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Jobs Count summary */}
              <div className="flex justify-between items-center text-xs text-slate-400 pt-2 border-t border-slate-50">
                <span>Showing {filteredJobs.length} of {jobs.length} total tickets</span>
                <span className="italic">ProTip: Click any row to view diagnostic logs and add notes.</span>
              </div>
            </div>

            {/* RMA Table Card */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50/75 border-b border-slate-150 text-xs font-bold uppercase tracking-wider text-slate-500">
                      <th className="py-4 px-6 w-32">RMA ID</th>
                      <th className="py-4 px-6">Customer Details</th>
                      <th className="py-4 px-6">Device Specs</th>
                      <th className="py-4 px-6">Diagnostics Summary</th>
                      <th className="py-4 px-6 w-24">Priority</th>
                      <th className="py-4 px-6 w-36">Status</th>
                      <th className="py-4 px-6 w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {filteredJobs.map((job) => (
                      <tr 
                        key={job.id} 
                        className="hover:bg-slate-50/50 transition-colors cursor-pointer group"
                        onClick={() => navigate(`/rma-jobs/${job.id}`)}
                      >
                        {/* ID */}
                        <td className="py-4 px-6 align-top">
                          <span className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                            {job.id}
                          </span>
                          <div className="text-[10px] text-slate-400 mt-1 whitespace-nowrap">
                            Created: {new Date(job.createdAt).toLocaleDateString()}
                          </div>
                        </td>

                        {/* Customer */}
                        <td className="py-4 px-6 align-top">
                          <div className="font-semibold text-slate-800">{job.customerName}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{job.customerPhone}</div>
                          <div className="text-[11px] text-slate-400 mt-0.5">{job.customerEmail}</div>
                        </td>

                        {/* Device */}
                        <td className="py-4 px-6 align-top">
                          <div className="flex items-center gap-1.5 font-semibold text-slate-700">
                            <span className="text-slate-400">{getCategoryIcon(job.deviceCategory)}</span>
                            {job.deviceBrand} {job.deviceModel}
                          </div>
                          <div className="text-xs text-slate-400 mt-1">S/N: {job.serialNumber}</div>
                        </td>

                        {/* Diagnostics & Problem */}
                        <td className="py-4 px-6 align-top max-w-[280px]">
                          <div className="text-xs text-slate-700 font-medium line-clamp-1">
                            <span className="font-bold text-slate-500 uppercase tracking-wider text-[9px] mr-1">Reported:</span>
                            {job.reportedProblem}
                          </div>
                          <div className="text-xs text-slate-500 italic mt-1 line-clamp-2">
                            <span className="font-bold text-slate-400 uppercase tracking-wider text-[9px] mr-1">Diagnostic Note:</span>
                            {job.diagnostics || 'No technical diagnostics filed yet.'}
                          </div>
                        </td>

                        {/* Priority */}
                        <td className="py-4 px-6 align-top">
                          <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-md border ${getPriorityBadgeClass(job.priority)}`}>
                            {job.priority}
                          </span>
                        </td>

                        {/* Status dropdown trigger */}
                        <td 
                          className="py-4 px-6 align-top relative"
                          onClick={(e) => e.stopPropagation()} // Stop row navigate
                        >
                          <div className="relative">
                            <button
                              onClick={() => setActiveMenuId(activeMenuId === job.id ? null : job.id)}
                              className={`text-[11px] font-bold px-3 py-1.5 rounded-xl border flex items-center justify-between w-32 hover:shadow-sm transition-all ${getStatusBadgeClass(job.status)}`}
                            >
                              <span>{job.status}</span>
                              <SlidersHorizontal className="h-3 w-3 ml-1 text-slate-400" />
                            </button>
                            
                            {/* Inline popup selector */}
                            {activeMenuId === job.id && (
                              <div className="absolute left-0 mt-1 w-40 rounded-xl bg-white border border-slate-100 shadow-xl z-20 py-1 divide-y divide-slate-50">
                                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">Change Status</div>
                                <div className="py-1">
                                  {statuses.map((s) => (
                                    <button
                                      key={s}
                                      onClick={() => handleStatusChange(job.id, s)}
                                      className={`block w-full text-left px-3 py-1.5 text-xs transition-colors ${
                                        job.status === s 
                                          ? 'bg-blue-50 text-blue-600 font-bold' 
                                          : 'text-slate-600 hover:bg-slate-50'
                                      }`}
                                    >
                                      {s}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Actions */}
                        <td 
                          className="py-4 px-6 align-top text-right"
                          onClick={(e) => e.stopPropagation()} // Stop row navigate
                        >
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => navigate(`/rma-jobs/${job.id}`)}
                              className="p-2 text-slate-400 hover:text-blue-600 hover:bg-slate-100 rounded-lg transition-colors"
                              title="Edit Repair Notes"
                            >
                              <Eye className="h-4.5 w-4.5" />
                            </button>
                            <button
                              onClick={() => navigate(`/rma-jobs/${job.id}?print=true`)}
                              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                              title="Print Ticket"
                            >
                              <Printer className="h-4.5 w-4.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredJobs.length === 0 && (
                      <tr>
                        <td colSpan={7} className="py-12 text-center text-slate-400">
                          <p className="font-semibold text-slate-500">No matching tickets found</p>
                          <p className="text-xs text-slate-400 mt-1">Try adjusting search term or dropdown filters.</p>
                          <button
                            onClick={resetFilters}
                            className="mt-3 text-xs text-blue-600 hover:underline font-bold"
                          >
                            Reset Filters
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
};

export default RmaJobs;
